import React from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormGroup, FormGroupLabel, ErrorMessage, RequiredMark } from './BaseInput';
import { colorAccent, colorAdditional, colorBackground, colorFieldsBorder, colorIcon, colorText, colorBackgroundBody } from '../../theme/palette';
import { applyTextStyle } from '../../theme/typography';

const SelectField = ({
  name,
  label,
  placeholder,
  options = [],
  value,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  required = false,
  error,
  isMulti = false,
  ...props
}) => {
  // Sempre encontrar o objeto da opção correspondente ao valor primitivo
  const currentValue = React.useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object' && value.value !== undefined) return value; // Procurar o objeto da opção correspondente ao valor primitivo

    return options.find(opt => String(opt.value) === String(value)) || null;
  }, [value, options]); // Format options for react-select

  const formattedOptions = React.useMemo(() => {
    return options.map(option => ({
      value: option.value || option.id || option,
      label: option.label || option.nome || option.name || String(option)
    }));
  }, [options]); // Handle option selection

  const handleChange = selectedOption => {
    if (onChange) {
      // Passar o objeto inteiro para o form state
      onChange(selectedOption);
    }
  }; // Handle blur event


  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  }; // Handle focus event


  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  return <FormGroup>
      {label && <FormGroupLabel>
          {label}
          {required && <RequiredMark>*</RequiredMark>}
        </FormGroupLabel>}
      <StyledSelect name={name} value={currentValue} onChange={handleChange} onBlur={handleBlur} onFocus={handleFocus} options={formattedOptions} placeholder={placeholder || 'Selecione uma opção'} isDisabled={disabled} isMulti={isMulti} isClearable={!required} isSearchable={true} className="react-select" classNamePrefix="react-select" noOptionsMessage={() => "Nenhuma opção disponível"} {...props} />
      {error && <ErrorMessage>
          {error}
        </ErrorMessage>}
    </FormGroup>;
};

SelectField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  isMulti: PropTypes.bool
};
export default SelectField; // region STYLES

const StyledSelect = styled(Select)`
  width: 100%;
  ${applyTextStyle('body')}

  .react-select__control {
    height: 32px;
    border-radius: 0 !important;
    transition: all 0.3s;
    border: 1px solid ${colorFieldsBorder};
    background-color: ${colorBackground};

    &.react-select__control--is-focused, &:hover {
      border-color: ${colorAccent} !important;
      box-shadow: none;
      background: transparent;
    }

    &.react-select__control--is-disabled {
      background-color: ${colorBackgroundBody}; 
      border-color: ${colorFieldsBorder};
    }
  }

  .react-select__input {
    height: 30px;
    color: ${colorText};
    ${applyTextStyle('body')}
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator, .react-select__clear-indicator {
    cursor: pointer;
    color: ${colorIcon};

    svg {
      height: 16px;
      width: 16px;
    }
  }

  .react-select__multi-value {
    background-color: transparent;
    border: 1px solid ${colorAccent};

    .react-select__multi-value__label {
      padding: 3px 6px;
      border-right: 1px solid ${colorAccent};
      color: ${colorText};
      ${applyTextStyle('caption')}
    }
  }

  .react-select__multi-value__remove {
    opacity: 0.8;
    transition: 0.3s;
    cursor: pointer;
    color: ${colorText};
  }

  .react-select__multi-value__label, .react-select__multi-value__remove {
    background: ${colorBackground};
  }

  .react-select__single-value {
    color: ${colorText};
    ${applyTextStyle('body')}
  }

  .react-select__menu {
    box-shadow: none !important;
    margin-top: 6px;
    margin-bottom: 6px;
  }

  .react-select__menu-list {
    top: calc(100% + 1px);
    border-radius: 0;
    box-shadow: none;
    ${applyTextStyle('body')}
    overflow: hidden;
    background: ${colorBackground};
    border: 1px solid ${colorFieldsBorder};
  }

  .react-select__option {
    padding: 6px 10px;
    cursor: pointer;
    transition: background-color 0.2s;
    ${applyTextStyle('body')}

    &:hover {
      background-color: ${colorFieldsBorder};
    }

    &.react-select__option--is-focused {
      background-color: ${colorFieldsBorder};
    }

    &.react-select__option--is-selected {
      background-color: ${colorAccent};
      color: white;
    }
  }

  .react-select__placeholder {
    ${applyTextStyle('body')}
    color: ${colorAdditional};
    margin-top: -2px;
  }
  
  .react-select__value-container {
    padding-top: 0;
    padding-bottom: 0;
    
    & > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
`; // endregion